/**
 * The one client, built once, at module scope.
 *
 * `InventoryProvider` memoises on the identity of its `config` object, so a config rebuilt
 * on every render would rebuild the client on every render. Building it here instead of in
 * a component sidesteps that entirely, and it is honest about what the object is: there is
 * exactly one inventory service and one client for it, for the life of the page.
 *
 * The token is read through a mutable cell rather than captured, because `getToken` is
 * called on **every** request — the core's transport says so explicitly, so that a host
 * refreshing tokens gets the fresh one without the module caching anything. A shell that
 * closed over the token at construction would keep presenting the first one it ever saw.
 */

import { InventoryClient, type UnauthorizedError } from '@inai/inventory-core';
import type { HostLinks } from '@inai/inventory-core/react';

import { SHELL_CONFIG } from './config';

const tokenCell: { current: string | null } = { current: null };

/** True when the last request carried a bearer token. The `diagnose` input. */
export function tokenWasSent(): boolean {
  return tokenCell.current !== null;
}

export function setBearerToken(token: string | null): void {
  tokenCell.current = token;
}

type UnauthorizedListener = (error: UnauthorizedError) => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/**
 * Watch for 401s the transport saw.
 *
 * Note what this is NOT for. The transport calls `onUnauthorized` and then throws anyway,
 * so every query and mutation still sees the error and the screens still render it. This
 * exists so the shell can react to a 401 that arrives from a request it did not itself
 * make — a background refetch, a mutation deep inside a screen — without every one of
 * those call sites having to know about the session.
 */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

/**
 * Deep links for the masters this module only mirrors (invariant 6).
 *
 * Standalone there may be no Sapiens and no CRM, in which case nothing is configured and
 * `MirroredMasterLink` renders plain text. That is the correct degradation and it is why
 * `HostLinks` makes every builder optional: a dead link is worse than no link.
 */
const hostLinks: HostLinks = {};
if (SHELL_CONFIG.sapiensUrl) {
  const base = SHELL_CONFIG.sapiensUrl.replace(/\/$/, '');
  hostLinks.employee = (id) => `${base}/employees/${encodeURIComponent(id)}`;
}
if (SHELL_CONFIG.crmUrl) {
  const base = SHELL_CONFIG.crmUrl.replace(/\/$/, '');
  hostLinks.customer = (id) => `${base}/customers/${encodeURIComponent(id)}`;
}
hostLinks.route = (path: string) => (path.startsWith('/') ? path : `/${path}`);

export const HOST_LINKS: HostLinks = hostLinks;

export const inventoryClient = new InventoryClient({
  baseUrl: SHELL_CONFIG.apiBaseUrl,
  getToken: () => tokenCell.current,
  onUnauthorized: (error) => {
    for (const listener of unauthorizedListeners) listener(error);
  },
  onReplay: ({ path, key }) => {
    // Worth seeing in a support session: it means a retry was correctly deduplicated
    // rather than posting stock a second time (invariant 3).
    // eslint-disable-next-line no-console
    console.info('[inai-inventory] replayed %s (idempotency key %s)', path, key);
  },
});
